// =============================================================================
// SIREPPAR - Control de Admisibilidad
// =============================================================================

import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api } from '../../services/api';
import toast from 'react-hot-toast';
import RichTextEditor, { MarkdownDisplay } from '../../components/common/RichTextEditor';
import { tipoTramiteLabel } from '../../utils/tiposExpediente';
import { useAuth } from '../../contexts/AuthContext';
import ModalInformeSimple, { type InformeSimpleConfig } from '../../components/common/ModalInformeSimple';
import { textoInformeNoPresentado } from '../../utils/noPresentado';

interface Expediente {
  id: string;
  numero: string;
  tipo: string;
  estado: string;
  fechaCreacion: string;
  fechaAsignacion?: string;
  diasTranscurridos: number;
  diasHabilesTranscurridos: number;
  vencido: boolean;
  prioridad: 'ALTA' | 'MEDIA' | 'NORMAL';
  partido?: { denominacion: string; sigla?: string; numero?: number };
  creador?: { nombre: string; apellido: string; email: string };
  asignado?: { nombre: string; apellido: string };
}

interface ExpedienteIntimado extends Expediente {
  fechaIntimacion?: string;
  diasRestantes: number;
  observaciones?: Array<{ texto: string; creadoEn: string; respuestaTexto?: string; respuestaFecha?: string; estado?: string }>;
}

const estadoConfig: Record<string, { color: string; label: string }> = {
  PRESENTADO: { color: 'bg-blue-100 text-blue-800', label: 'Presentado' },
  EN_ADMISIBILIDAD: { color: 'bg-purple-100 text-purple-800', label: 'En Admisibilidad' },
  INTIMADO: { color: 'bg-orange-100 text-orange-800', label: 'Intimado' },
  NO_PRESENTADO: { color: 'bg-red-100 text-red-800', label: 'No Presentado' },
};

const prioridadConfig: Record<string, { color: string; label: string }> = {
  ALTA: { color: 'bg-red-500 text-white', label: 'URGENTE' },
  MEDIA: { color: 'bg-yellow-500 text-white', label: 'Próximo a vencer' },
  NORMAL: { color: 'bg-green-500 text-white', label: 'En plazo' },
};

export default function AdmisibilidadPage() {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const [tab, setTab] = useState<'cola' | 'intimados' | 'respondidas'>('cola');
  const [modalIntimar, setModalIntimar] = useState<string | null>(null);
  // Expediente para el que se está redactando el informe de "no presentado".
  const [noPresentadoCtx, setNoPresentadoCtx] = useState<ExpedienteIntimado | null>(null);
  const [observaciones, setObservaciones] = useState('');

  // Query cola de admisibilidad
  const { data: colaData, isLoading: loadingCola } = useQuery({
    queryKey: ['admisibilidad', 'cola'],
    queryFn: async () => {
      const res = await api.get('/admisibilidad/cola');
      return res.data.data;
    },
  });

  // Query intimados
  const { data: intimadosData, isLoading: loadingIntimados } = useQuery({
    queryKey: ['admisibilidad', 'intimados'],
    queryFn: async () => {
      const res = await api.get('/admisibilidad/intimados');
      return res.data.data;
    },
  });
  // Query respondidas
  const { data: respondidasData, isLoading: loadingRespondidas } = useQuery({
    queryKey: ["admisibilidad", "respondidas"],
    queryFn: async () => {
      const res = await api.get("/admisibilidad/respondidas");
      return res.data.data;
    },
  });

  // Mutation: Iniciar admisibilidad
  const iniciarMutation = useMutation({
    mutationFn: (id: string) => api.post(`/admisibilidad/${id}/iniciar`),
    onSuccess: () => {
      toast.success('Control de admisibilidad iniciado');
      queryClient.invalidateQueries({ queryKey: ['admisibilidad'] });
    },
    onError: (e: any) => toast.error(e.response?.data?.error?.message || 'Error'),
  });

  // Mutation: Aprobar admisibilidad
  const aprobarMutation = useMutation({
    mutationFn: (id: string) => api.post(`/admisibilidad/${id}/aprobar`),
    onSuccess: () => {
      toast.success('Admisibilidad aprobada - Expediente pasa a análisis');
      queryClient.invalidateQueries({ queryKey: ['admisibilidad'] });
    },
    onError: (e: any) => toast.error(e.response?.data?.error?.message || 'Error'),
  });

  // Mutation: Intimar
  const intimarMutation = useMutation({
    mutationFn: ({ id, observaciones }: { id: string; observaciones: string }) => 
      api.post(`/admisibilidad/${id}/intimar`, { observaciones }),
    onSuccess: () => {
      toast.success('Intimación enviada al partido');
      setModalIntimar(null);
      setObservaciones('');
      queryClient.invalidateQueries({ queryKey: ['admisibilidad'] });
    },
    onError: (e: any) => toast.error(e.response?.data?.error?.message || 'Error'),
  });

  // Mutation: Subsanar
  const subsanarMutation = useMutation({
    mutationFn: (id: string) => api.post(`/admisibilidad/${id}/subsanar`),
    onSuccess: () => {
      toast.success('Intimación subsanada - Vuelve a admisibilidad');
      queryClient.invalidateQueries({ queryKey: ['admisibilidad'] });
    },
    onError: (e: any) => toast.error(e.response?.data?.error?.message || 'Error'),
  });

  // "No presentado": no archiva el expediente. Abre el modal del informe al
  // Secretario (con el historial de intimaciones al costado) y el expediente pasa
  // a NO_PRESENTADO, a resolución del Secretario en la columna Resolución.
  const configNoPresentado: InformeSimpleConfig | null = noPresentadoCtx && {
    basePath: 'no-presentado',
    endpoint: 'elevar-secretario',
    titulo: `Dar por no presentada la solicitud — ${noPresentadoCtx.numero}`,
    textoInicial: textoInformeNoPresentado(noPresentadoCtx),
    ayuda:
      'Se tiene por no presentada la solicitud y se eleva el informe al Secretario Electoral, que lo verá ' +
      'en la columna Resolución. Tildá al costado las intimaciones que quieras mostrarle: se agregan al ' +
      'final del informe, de la más antigua a la más reciente.',
    confirmMsg:
      '¿Tener por NO PRESENTADA la solicitud y elevar el informe al Secretario?\n\n' +
      'El expediente pasa a "No Presentado" y queda a resolución del Secretario Electoral.',
    successMsg: 'Solicitud dada por no presentada. Informe elevado al Secretario.',
    conHistorial: true,
    // SIN check de aviso al partido: el informe al Secretario es de la vía INTERNA
    // del organismo (SEP → Secretario). Al partido no se le manda ni el PDF ni el
    // aviso de que el expediente avanzó — el paso no le pide nada ni resuelve nada,
    // y el aviso lo dejaba esperando una novedad que todavía no existe. Se le
    // notifica cuando hay una decisión (resolución) o algo que responder
    // (observación/intimación). Vale para TODOS los trámites.
  };

  const cola: Expediente[] = colaData || [];
  const intimados: ExpedienteIntimado[] = intimadosData || [];
  const respondidas: ExpedienteIntimado[] = respondidasData || [];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Control de Admisibilidad</h1>
        <p className="text-gray-500 mt-1">
          Verificación de requisitos formales dentro de 5 días hábiles
        </p>
      </div>

      {/* Estadísticas */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg shadow p-4">
          <p className="text-sm text-gray-500">En Cola</p>
          <p className="text-2xl font-bold">{cola.length}</p>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <p className="text-sm text-gray-500">Urgentes</p>
          <p className="text-2xl font-bold text-red-600">
            {cola.filter(e => e.prioridad === 'ALTA').length}
          </p>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <p className="text-sm text-gray-500">Intimados</p>
          <p className="text-2xl font-bold text-orange-600">{intimados.length}</p>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <p className="text-sm text-gray-500">Intimaciones Vencidas</p>
          <p className="text-2xl font-bold text-red-600">
            {intimados.filter(e => e.vencido).length}
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setTab('cola')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              tab === 'cola'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Cola de Admisibilidad ({cola.length})
          </button>
          <button
            onClick={() => setTab('intimados')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              tab === 'intimados'
                ? 'border-orange-500 text-orange-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Intimados ({intimados.length})
          </button>
          <button
            onClick={() => setTab('respondidas')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              tab === 'respondidas'
                ? 'border-green-500 text-green-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Respondidas ({respondidas.length})
          </button>
        </nav>
      </div>

      {/* Tab: Cola */}
      {tab === 'cola' && (
        <div className="bg-white shadow rounded-lg overflow-hidden">
          {loadingCola ? (
            <div className="p-8 text-center text-gray-500">Cargando...</div>
          ) : cola.length === 0 ? (
            <div className="p-8 text-center text-gray-500">No hay expedientes pendientes</div>
          ) : (
            <div className="overflow-x-auto"><table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Prioridad</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Expediente</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Partido</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Estado</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Días</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Asignado</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Acciones</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {cola.map((exp) => (
                  <tr key={exp.id} className={exp.vencido ? 'bg-red-50' : ''}>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 text-xs font-bold rounded ${prioridadConfig[exp.prioridad]?.color}`}>
                        {prioridadConfig[exp.prioridad]?.label}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <Link to={`/expedientes/${exp.id}`} className="text-blue-600 hover:underline font-medium">
                        {exp.numero}
                      </Link>
                      <p className="text-xs text-gray-500">{tipoTramiteLabel(exp.tipo)}</p>
                    </td>
                    <td className="px-4 py-3">
                      <p className="font-medium">{exp.partido?.denominacion}</p>
                      <p className="text-xs text-gray-500">{exp.partido?.sigla} - N° {exp.partido?.numero}</p>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 text-xs rounded-full ${estadoConfig[exp.estado]?.color}`}>
                        {estadoConfig[exp.estado]?.label}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <p className={`font-bold ${exp.vencido ? 'text-red-600' : ''}`}>
                        {exp.diasHabilesTranscurridos} días háb.
                      </p>
                      <p className="text-xs text-gray-500">{exp.diasTranscurridos} corridos</p>
                    </td>
                    <td className="px-4 py-3 text-sm">
                      {exp.asignado ? `${exp.asignado.nombre} ${exp.asignado.apellido}` : '-'}
                    </td>
                    <td className="px-4 py-3 text-right space-x-2">
                      {exp.estado === 'PRESENTADO' && (
                        <button
                          onClick={() => iniciarMutation.mutate(exp.id)}
                          className="px-3 py-1 bg-purple-600 text-white text-sm rounded hover:bg-purple-700"
                        >
                          Iniciar
                        </button>
                      )}
                      {exp.estado === 'EN_ADMISIBILIDAD' && (
                        <>
                          <button
                            onClick={() => aprobarMutation.mutate(exp.id)}
                            className="px-3 py-1 bg-green-600 text-white text-sm rounded hover:bg-green-700"
                          >
                            Aprobar
                          </button>
                          <button
                            onClick={() => setModalIntimar(exp.id)}
                            disabled={intimarMutation.isPending}
                            className="px-3 py-1 bg-orange-600 text-white text-sm rounded hover:bg-orange-700 disabled:opacity-50"
                          >
                            Intimar
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table></div>
          )}
        </div>
      )}

      {/* Tab: Intimados */}
      {tab === 'intimados' && (
        <div className="bg-white shadow rounded-lg overflow-hidden">
          {loadingIntimados ? (
            <div className="p-8 text-center text-gray-500">Cargando...</div>
          ) : intimados.length === 0 ? (
            <div className="p-8 text-center text-gray-500">No hay expedientes intimados</div>
          ) : (
            <div className="overflow-x-auto"><table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Expediente</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Partido</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Plazo</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Observaciones</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Acciones</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {intimados.map((exp) => (
                  <tr key={exp.id} className={exp.vencido ? 'bg-red-50' : ''}>
                    <td className="px-4 py-3">
                      <Link to={`/expedientes/${exp.id}`} className="text-blue-600 hover:underline font-medium">
                        {exp.numero}
                      </Link>
                      <p className="text-xs text-gray-500">{tipoTramiteLabel(exp.tipo)}</p>
                    </td>
                    <td className="px-4 py-3">
                      <p className="font-medium">{exp.partido?.denominacion}</p>
                      <p className="text-xs text-gray-500">{exp.creador?.email}</p>
                    </td>
                    <td className="px-4 py-3">
                      {exp.vencido ? (
                        <span className="px-2 py-1 bg-red-100 text-red-800 text-xs rounded-full font-bold">
                          VENCIDO
                        </span>
                      ) : (
                        <span className="font-bold text-orange-600">
                          {exp.diasRestantes} días restantes
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 max-w-xs">
                      <p className="text-sm text-gray-600 truncate">
                      {(exp.observaciones?.[0]?.texto || '').replace(/[#*_`>\-\[\]]/g, '').slice(0, 80) || '-'}
                      </p>
                    </td>
                    <td className="px-4 py-3 text-right space-x-2">
                      <button
                        onClick={() => {
                          if (confirm('¿Aprobar la admisibilidad? El expediente pasará a análisis.')) {
                            aprobarMutation.mutate(exp.id);
                          }
                        }}
                        className="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700"
                      >
                        Aprobar
                      </button>
                      <button
                        onClick={() => subsanarMutation.mutate(exp.id)}
                        className="px-3 py-1 bg-green-600 text-white text-sm rounded hover:bg-green-700"
                      >
                        Subsanado
                      </button>
                      {user?.rol !== 'MESA_ENTRADAS' && (
                        <button
                          onClick={() => setModalIntimar(exp.id)}
                          disabled={intimarMutation.isPending}
                          className="px-3 py-1 bg-orange-600 text-white text-sm rounded hover:bg-orange-700 disabled:opacity-50"
                        >
                          + Observación
                        </button>
                      )}
                      {/* Venció el plazo y el partido nunca subsanó: no se archiva.
                          Se redacta el informe al Secretario y el expediente pasa a
                          "No Presentado", a resolución del Secretario. */}
                      {exp.vencido && user?.rol !== 'MESA_ENTRADAS' && (
                        <button
                          onClick={() => setNoPresentadoCtx(exp)}
                          className="px-3 py-1 bg-red-600 text-white text-sm rounded hover:bg-red-700"
                        >
                          No Presentado
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table></div>
          )}
        </div>
      )}

      {/* Tab: Respondidas */}
      {tab === 'respondidas' && (
        <div className="bg-white shadow rounded-lg overflow-hidden">
          {loadingRespondidas ? (
            <div className="p-8 text-center text-gray-500">Cargando...</div>
          ) : respondidas.length === 0 ? (
            <div className="p-8 text-center text-gray-500">No hay intimaciones respondidas pendientes de revisión</div>
          ) : (
            <div className="overflow-x-auto"><table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Expediente</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Partido</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Intimación</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Respuesta</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Acciones</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {respondidas.map((exp) => (
                  <tr key={exp.id} className="bg-green-50">
                    <td className="px-4 py-3">
                      <Link to={`/expedientes/${exp.id}`} className="text-blue-600 hover:underline font-medium">
                        {exp.numero}
                      </Link>
                      <p className="text-xs text-gray-500">{tipoTramiteLabel(exp.tipo)}</p>
                    </td>
                    <td className="px-4 py-3">
                      <p className="font-medium">{exp.partido?.denominacion}</p>
                    </td>
                    <td className="px-4 py-3 max-w-xs">
                      <p className="text-sm text-gray-600 truncate">{(exp.observaciones?.[0]?.texto || '').replace(/[#*_`>\-\[\]]/g, '').slice(0, 80) || '-'}</p>
                    </td>
                    <td className="px-4 py-3 max-w-xs">
  <p className="text-sm text-green-700 truncate">{(exp.observaciones?.[0]?.respuestaTexto || '').replace(/[#*_`>\-\[\]]/g, '').slice(0, 80) || '-'}</p>
  <p className="text-xs text-gray-500">{exp.observaciones?.[0]?.respuestaFecha ? new Date(exp.observaciones[0].respuestaFecha).toLocaleDateString('es-AR') : ''}</p>
  {(() => {
    const respuestaFecha = exp.observaciones?.[0]?.respuestaFecha;
    if (!respuestaFecha) return null;
    // Los docs de subsanación tienen título que empieza con 'Subsanación:'
    // y están en el expediente — necesitamos traerlos desde la API
    return (
      <Link
        to={`/expedientes/${exp.id}`}
        className="text-xs text-blue-600 hover:underline mt-1 inline-block"
      >
        📎 Ver archivos adjuntos
      </Link>
    );
  })()}
</td>
                    <td className="px-4 py-3 text-right space-x-2">
                      <button
                        onClick={() => subsanarMutation.mutate(exp.id)}
                        className="px-3 py-1 bg-green-600 text-white text-sm rounded hover:bg-green-700"
                      >
                        Aprobar
                      </button>
                      <button
                        onClick={() => setModalIntimar(exp.id)}
                        className="px-3 py-1 bg-orange-600 text-white text-sm rounded hover:bg-orange-700"
                      >
                        Reintimar
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table></div>
          )}
        </div>
      )}
      {/* Modal Intimar */}
      {modalIntimar && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-lg">
          <h3 className="text-lg font-bold mb-4">
  {(intimados.some(e => e.id === modalIntimar) || respondidas.some(e => e.id === modalIntimar))
    ? 'Agregar observación a intimación existente'
    : 'Intimar por Documentación Incompleta'}
    </h3>
    <p className="text-sm text-gray-600 mb-4">
      {(intimados.some(e => e.id === modalIntimar) || respondidas.some(e => e.id === modalIntimar))
        ? 'Se agregará una nueva observación. El plazo del partido se reiniciará desde hoy (10 días hábiles).'
        : 'El partido tendrá 10 días hábiles para subsanar. Si no lo hace, se tendrá por no presentada la solicitud.'}
    </p>
            <RichTextEditor
              value={observaciones}
              onChange={setObservaciones}
              placeholder="Detalle los documentos faltantes o las observaciones..."
              minHeight={150}
            />
            <div className="flex justify-end space-x-3 mt-4">
              <button
                onClick={() => { setModalIntimar(null); setObservaciones(''); }}
                className="px-4 py-2 border rounded-lg hover:bg-gray-50"
              >
                Cancelar
              </button>
              <button
                onClick={() => intimarMutation.mutate({ id: modalIntimar, observaciones })}
                disabled={!observaciones.trim() || intimarMutation.isPending}
                className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50"
              >
                {intimarMutation.isPending ? 'Enviando...' : 'Enviar Intimación'}
              </button>
            </div>
          </div>
        </div>
      )}

      <ModalInformeSimple
        config={configNoPresentado}
        expedienteId={noPresentadoCtx?.id || ''}
        onClose={() => setNoPresentadoCtx(null)}
        onGuardado={() => {
          queryClient.invalidateQueries({ queryKey: ['admisibilidad'] });
          queryClient.invalidateQueries({ queryKey: ['gestion-expedientes'] });
        }}
      />
    </div>
  );
}
